import React, { useState } from 'react';
import { Search, Download, Edit, RotateCcw, X } from 'lucide-react';
import dicomImage from './assets/dicom-image.jpg';
import EditPopup from './EditPopup';

const RetrievePatientData = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [showEditPopup, setShowEditPopup] = useState(false);

  // Sample data
  const patients = [
    { 
      id: 1, 
      patientCode: 'PT001', 
      firstName: 'Rajesh', 
      lastName: 'Kumar', 
      age: 35, 
      gender: 'Male',
      date: '2023-10-15',
      time: '10:30 AM',
      doctorName: 'Dr. John Smith',
      tests: [
        { 
          id: 1, 
          name: 'Complete Blood Count (CBC)', 
          status: 'completed',
          files: [
            { id: 1, type: 'DICOM', name: 'scan_001.dcm', url: dicomImage, imageUrl: dicomImage },
            { id: 2, type: 'JPEG', name: 'scan_image.jpg', url: dicomImage, imageUrl: dicomImage },
            { id: 3, type: 'Metadata', name: 'scan_metadata.xml', url: 'https://example.com/scan1_meta.xml' }
          ]
        },
        { 
          id: 2, 
          name: 'Blood Glucose Fasting', 
          status: 'completed',
          files: [
            { id: 4, type: 'DICOM', name: 'glucose_001.dcm', url: dicomImage, imageUrl: dicomImage },
            { id: 5, type: 'JPEG', name: 'glucose_image.jpg', url: dicomImage, imageUrl: dicomImage }
          ]
        }
      ]
    },
    { 
      id: 2, 
      patientCode: 'PT002', 
      firstName: 'Priya', 
      lastName: 'Sharma', 
      age: 28, 
      gender: 'Female',
      date: '2023-10-16',
      time: '02:15 PM',
      doctorName: 'Dr. John Smith',
      tests: [
        { 
          id: 3, 
          name: 'Thyroid Profile', 
          status: 'completed',
          files: [
            { id: 6, type: 'DICOM', name: 'thyroid_001.dcm', url: dicomImage, imageUrl: dicomImage },
            { id: 7, type: 'JPEG', name: 'thyroid_image.jpg', url: dicomImage, imageUrl: dicomImage },
            { id: 8, type: 'Metadata', name: 'thyroid_metadata.xml', url: 'https://example.com/thyroid1_meta.xml' }
          ]
        }
      ]
    }
  ];

  const filteredPatients = patients.filter(patient => 
    patient.patientCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleImageSelect = (file) => {
    setSelectedImage(file);
    setRotation(0);
    setShowEditPopup(false);
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setRotation(0);
  };

  const handleDownload = (file) => {
    // Create a temporary link to download the file
    const link = document.createElement('a');
    link.href = file.url || file.imageUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Retrieve Patient Data</h2>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
        >
          Back to Dashboard
        </button>
      </div>

      {!selectedPatient ? (
        <div>
          <div className="mb-6 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by patient code, first name, or last name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            {filteredPatients.map(patient => (
              <div key={patient.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedPatient(patient)}>
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-lg">{patient.firstName} {patient.lastName}</h3>
                    <p className="text-gray-600">Code: {patient.patientCode} | Age: {patient.age} | Gender: {patient.gender}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{patient.tests.length} test(s)</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : !selectedImage ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">
              {selectedPatient.firstName} {selectedPatient.lastName}'s Test Results
            </h3>
            <button
              onClick={() => setSelectedPatient(null)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Back to Search
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {selectedPatient.tests.map(test => (
              <div key={test.id} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-lg mb-3">{test.name}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {test.files.map(file => (
                    <div 
                      key={file.id} 
                      className="border border-gray-200 rounded p-3 hover:bg-blue-50 cursor-pointer flex flex-col items-center group"
                      onClick={() => handleImageSelect(file)}
                    >
                      <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center mb-2 overflow-hidden">
                        {(file.type === 'DICOM' || file.type === 'JPEG') && file.imageUrl ? (
                          <img 
                            src={file.imageUrl} 
                            alt={file.name}
                            className="w-full h-full object-cover rounded"
                          />
                        ) : (
                          <div className="text-center">
                            <div className="font-bold text-xs">{file.type}</div>
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-center break-all mb-1">{file.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">
              {selectedImage.name} - {selectedImage.type}
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={() => handleDownload(selectedImage)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </button>
              <button
                onClick={() => setSelectedImage(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Back to Results
              </button>
            </div>
          </div>

          <div className="bg-gray-100 rounded-lg p-4 mb-4">
            <div className="flex justify-center mb-4 overflow-auto">
              <div 
                className="bg-white p-2 shadow-md inline-block relative"
                style={{ 
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease'
                }}
              >
                {(selectedImage.type === 'JPEG' || selectedImage.type === 'DICOM') && selectedImage.imageUrl ? (
                  <>
                    <img 
                      src={selectedImage.imageUrl} 
                      alt={selectedImage.name}
                      className="max-w-full max-h-96 object-contain"
                    />
                  </>
                ) : (
                  <div className="w-96 h-64 flex items-center justify-center bg-gray-200">
                    <div className="text-center">
                      <div className="text-xl font-bold">{selectedImage.type} File</div>
                      <div className="text-gray-600">Preview not available</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-2">
              <button 
                onClick={handleRotate}
                className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center text-sm"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Rotate
              </button>
              <button 
                onClick={() => setShowEditPopup(true)}
                className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center text-sm"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit Image
              </button>
              <button 
                onClick={handleReset}
                className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center text-sm"
              >
                <X className="w-4 h-4 mr-1" />
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Image Popup */}
      {showEditPopup && (
        <EditPopup
          selectedImage={selectedImage}
          onSave={() => setShowEditPopup(false)}
          onCancel={() => setShowEditPopup(false)}
        />
      )}
    </div>
  );
};

export default RetrievePatientData;